/** @file Collects one block per facet entry that holds a loose tile image (drawable then entry order). */
import type { FacetEntry, SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { cellRectOf } from '@zilo/pose3d-export/cells/cellRectOf';
import { entryTiles } from '@zilo/pose3d-export/cells/entryTiles';
import { sheetTileOf } from '@zilo/pose3d-export/cells/sheetTileOf';
import { tableOf } from '@zilo/pose3d-export/resolve/tableOf';
import type { BlockTile, PackBlock } from '@zilo/pose3d-export/types/packBlock';

function blockOf(puppet: Puppet, entry: FacetEntry, key: string, tiles: number): PackBlock | undefined {
  const filled: Record<number, BlockTile> = {};
  let loose = false;
  for (let t = 0; t < tiles; t += 1) {
    const image = entry.slots[String(t)];
    if (image === undefined) continue;
    if (sheetTileOf(puppet, entry, t)) filled[t] = { image, from: cellRectOf(entry, t) };
    else { filled[t] = { image }; loose = true; }
  }
  if (!loose) return undefined;
  const [w, h] = entry.size;
  const extendX = entry.extendX === true;
  return { group: entry.sheet, key, tiles, size: extendX ? [w, tiles * h] : [tiles * w, h], tile: [w, h], extendX, filled };
}

/**
 * The blocks of `puppet`'s impostor entries that hold at least one loose tile image (flattened attachment parts
 * included, R1); a block keeps every filled tile, sheet-backed ones with their strip rect. Tables resolve through
 * `puppet.sectorTables` then `tables` (`SECTOR_TABLE_UNKNOWN` at the entry's `yaw` otherwise).
 */
export function collectBlocks(puppet: Puppet, tables: Readonly<Record<string, SectorTable>>): PackBlock[] {
  const blocks: PackBlock[] = [];
  puppet.drawables.forEach((d, i) => {
    if (d.kind !== 'impostor') return;
    d.facets.forEach((entry, e) => {
      const table = tableOf(entry.yaw, puppet, tables, `/puppet/drawables/${i}/facets/${e}/yaw`);
      const block = blockOf(puppet, entry, `${d.id}/${e}`, entryTiles(entry, table));
      if (block !== undefined) blocks.push(block);
    });
  });
  return blocks;
}
